import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { HighlightedPhrase } from '@openx/components-core/lib/HighlightedPhrase/HighlightedPhrase';
import { useTranslation } from 'react-i18next';
import type { ListCountriesQuery } from '../../../../../../../../../graphql/fragment/audiences/ListCountries.generated';
import type { ListRegionsQuery } from '../../../../../../../../../graphql/fragment/audiences/ListRegions.generated';
import { categoryBoxStyles, labelStyles, labelValuePairStyles } from './styles';
import { useMarketDisplayLabels } from './useMarketDisplayLabels';

export type DetailedInfoProps = {
  description: string;
  searchPhrase?: string;
  type: string;
  providerName: string;
  segmentType: string;
  segmentCategory: string;
  segmentSubCategory: string;
  regionCodes: string[];
  countryCodes: string[];
  isPredefined: boolean;
  regions: ListRegionsQuery['region'];
  countries: ListCountriesQuery['country'];
};

export const DetailedInfo = ({
  description,
  searchPhrase,
  type,
  providerName,
  segmentType,
  segmentCategory,
  segmentSubCategory,
  regionCodes,
  countryCodes,
  isPredefined,
  regions,
  countries,
}: DetailedInfoProps) => {
  const { t } = useTranslation();

  const { regionLabel, countryLabel } = useMarketDisplayLabels({
    regionCodes,
    countryCodes,
    regions,
    countries,
  });

  const providerValue = providerName === 'OpenAudience' || isPredefined ? 'OpenXSelect' : providerName || t('N / A');

  return (
    <Box
      sx={{
        ml: 4,
      }}
    >
      <Box sx={categoryBoxStyles}>
        <Typography
          variant="body2"
          component="span"
          sx={{
            color: 'text.secondary',
            mr: 0.5,
          }}
        >
          {t('Description:')}
        </Typography>
        {description.split(' ').map((word, index) => (
          <Typography
            variant="body2"
            component="span"
            key={index}
            sx={{
              mr: 0.5,
            }}
          >
            <HighlightedPhrase searchPhrase={searchPhrase}>{word}</HighlightedPhrase>
          </Typography>
        ))}
        <Box sx={labelValuePairStyles}>
          <Typography variant="body2" sx={labelStyles}>
            {t('Data Type:')}
          </Typography>
          <Typography variant="body2">{type || t('N / A')}</Typography>
        </Box>

        <Box sx={labelValuePairStyles}>
          <Typography variant="body2" sx={labelStyles}>
            {t('Category type:')}
          </Typography>
          <Typography variant="body2">{segmentType || t('N / A')}</Typography>
        </Box>

        <Box sx={labelValuePairStyles}>
          <Typography variant="body2" sx={labelStyles}>
            {t('Category:')}
          </Typography>
          <Typography variant="body2">{segmentCategory || t('N / A')}</Typography>
        </Box>

        <Box sx={labelValuePairStyles}>
          <Typography variant="body2" sx={labelStyles}>
            {t('Sub Category:')}
          </Typography>
          <Typography variant="body2">{segmentSubCategory || t('N / A')}</Typography>
        </Box>

        <Box sx={labelValuePairStyles}>
          <Typography variant="body2" sx={labelStyles}>
            {t('Data Provider:')}
          </Typography>
          <Typography variant="body2">{providerValue}</Typography>
        </Box>

        {regionCodes.length > 0 && (
          <Box sx={labelValuePairStyles}>
            <Typography variant="body2" sx={labelStyles}>
              {t('Region:')}
            </Typography>
            <Typography variant="body2">{regionLabel || t('N / A')}</Typography>
          </Box>
        )}

        {countryCodes.length > 0 && (
          <Box sx={labelValuePairStyles}>
            <Typography variant="body2" sx={labelStyles}>
              {t('Country:')}
            </Typography>
            <Typography variant="body2">{countryLabel || t('N / A')}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
